/**
 * Shared team color utilities
 * Used by: Schedule.jsx, GameBoxScore.jsx, Stats.jsx, LiveGame.jsx,
 *          TeamProfile.jsx, Teams.jsx, GameRecap.jsx, Players.jsx,
 *          PublicScoreboard.jsx, PlayoffBracket.jsx, PlayoffPredictions.jsx,
 *          Standings.jsx
 */

/**
 * Detect if dark mode is active (Tailwind class-based)
 * @returns {boolean}
 */
export const isDarkModeActive = () => {
  return document.documentElement.classList.contains("dark");
};

/**
 * Calculate perceived brightness of a hex color (0-255)
 * @param {string} hex - Hex color (with or without #)
 * @returns {number} - Brightness value 0-255
 */
const getBrightness = (hex) => {
  const clean = hex.replace("#", "");
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  // Perceived brightness formula (ITU-R BT.709)
  return r * 0.2126 + g * 0.7152 + b * 0.0722;
};

/**
 * Get contrasting text color (white or dark) for a given background hex
 * Takes dark mode into account
 * @param {string} accentHex - Hex color for background
 * @param {boolean} isDarkMode - Whether dark mode is active
 * @returns {string} - CSS color value (white or gray/dark)
 */
export const getTextColorForAccent = (accentHex, isDarkMode) => {
  const brightness = getBrightness(accentHex);
  // Threshold: if brightness > 128, use dark text; otherwise white
  if (brightness > 128) {
    return isDarkMode ? "#1f2937" : "#111827"; // dark gray or near-black
  }
  return "#ffffff"; // white
};

/**
 * Get a readable accent color style for table headers or emphasis
 * Returns inline style object with background + text color
 * @param {string} accentHex - Hex color for accent
 * @param {boolean} isDarkMode - Whether dark mode is active
 * @returns {Object} - { background, color }
 */
export const getAccentTextStyle = (accentHex, isDarkMode) => {
  return {
    background: accentHex,
    color: getTextColorForAccent(accentHex, isDarkMode),
  };
};

/**
 * Get team styling: header background, accent style, hex values
 * @param {Object} team - Team object with primary_color, secondary_color, gradient
 * @returns {Object} - { headerStyle, accentStyle, accentHex, primaryHex }
 */
export const getTeamStyle = (team) => {
  // 1. Establish fallbacks if fields are null/blank
  const rawPrimary = team?.primary_color || "374151";
  const rawSecondary = team?.secondary_color || "6b7280";

  // 2. Format with clean hashes
  const primary = rawPrimary.startsWith("#") ? rawPrimary : `#${rawPrimary}`;
  const secondary = rawSecondary.startsWith("#")
    ? rawSecondary
    : `#${rawSecondary}`;

  const isGradient = team?.gradient === true;

  return {
    headerStyle: {
      background: isGradient
        ? `linear-gradient(135deg, ${primary} 0%, ${secondary} 100%)`
        : primary,
    },
    // FIX: Default to primary color so white secondary trims don't make it look invisible
    accentStyle: isGradient
      ? {
          background: `linear-gradient(180deg, ${primary} 0%, ${secondary} 100%)`,
        }
      : { backgroundColor: primary },
    accentHex: secondary,
    primaryHex: primary,
  };
};

// Pick a team color (secondary preferred, then primary) that contrasts
// against a given background brightness; fall back to black/white text color.
export const getReadableTeamColor = (team, bgHex, isDarkMode) => {
  const { accentHex, primaryHex } = getTeamStyle(team);
  const bgB = getBrightness(bgHex);
  const contrasts = (hex) => Math.abs(getBrightness(hex) - bgB) >= 60;
  if (contrasts(accentHex)) return accentHex;
  if (contrasts(primaryHex)) return primaryHex;
  return getTextColorForAccent(bgHex, isDarkMode);
};
